import React, { useMemo, useState } from 'react';
import { BrioModal, BrioModalBody, BrioModalField, BrioInput, BrioButton } from '../../components/BrioComponents';

// Merge two library items. The row's Merge button opens this with that item as
// the first of the pair; the second is searched for here, then the curator
// picks which of the two is kept. The other item's versions move under the
// kept one as archived versions, below a divider naming their old code, and
// the other code is retired (boq.merge_library_items).
//
// `candidates` is [{ itemCode, description, versionCount }] for every live
// item; the source itself is left out here.
const MAX_LIST = 40;

function MergeItemsModal({ source, candidates, onMerge, onClose }) {
  const sourceCode = source.item.itemCode;
  const [query, setQuery] = useState('');
  const [otherCode, setOtherCode] = useState(null);
  const [keepCode, setKeepCode] = useState(sourceCode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const others = useMemo(() => candidates.filter((c) => c.itemCode !== sourceCode), [candidates, sourceCode]);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? others.filter((c) => c.itemCode.toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q))
      : others.filter((c) => c.itemCode.slice(0, 6) === sourceCode.slice(0, 6));   // same TRADE.SUBTRADE first
    return list.slice(0, MAX_LIST);
  }, [others, query, sourceCode]);

  const other = others.find((c) => c.itemCode === otherCode) || null;
  const pair = other
    ? [{ itemCode: sourceCode, description: source.description, versionCount: source.versionCount }, other]
    : [];
  const kept = pair.find((p) => p.itemCode === keepCode) || null;
  const absorbed = pair.find((p) => p.itemCode !== keepCode) || null;

  const handleMerge = async () => {
    if (!kept || !absorbed) return;
    setError(null);
    setSubmitting(true);
    const res = await onMerge(kept.itemCode, absorbed.itemCode);
    setSubmitting(false);
    if (res.success) onClose({ keptCode: kept.itemCode });
    else setError(res.error || 'Could not merge these items.');
  };

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={() => onClose()} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleMerge} disabled={submitting || !kept || !absorbed}>
        {submitting ? 'Merging…' : 'Merge items'}
      </BrioButton>
    </>
  );

  const rowStyle = (selected) => ({
    display: 'flex', gap: '0.6rem', alignItems: 'baseline', width: '100%', textAlign: 'left',
    padding: '0.35rem 0.5rem', border: 'none', borderBottom: '1px solid var(--brio-border-subtle, #eee)',
    background: selected ? 'rgba(159,144,42,0.12)' : 'transparent', cursor: 'pointer', fontSize: '0.82rem',
  });

  return (
    <BrioModal title={<>Merge <code>{sourceCode}</code> with another item</>} onClose={() => onClose()} width="md" footer={footer}>
      <BrioModalBody form>
        {error && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <BrioModalField label="Item to merge with" hint="Search by code or description. Before you type, items under the same trade and sub-trade are listed.">
          <BrioInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Code or description" disabled={submitting} />
        </BrioModalField>
        <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, marginBottom: '1rem' }}>
          {matches.length === 0 ? (
            <div style={{ padding: '0.5rem', fontSize: '0.8rem', color: 'var(--brio-text-muted)' }}>No items match.</div>
          ) : matches.map((c) => (
            <button key={c.itemCode} type="button" onClick={() => setOtherCode(c.itemCode)} disabled={submitting} style={rowStyle(c.itemCode === otherCode)}>
              <code style={{ fontWeight: 600, flexShrink: 0 }}>{c.itemCode}</code>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.description || '—'}</span>
              <span style={{ marginLeft: 'auto', flexShrink: 0, color: 'var(--brio-text-muted)', fontSize: '0.74rem' }}>
                {c.versionCount} version{c.versionCount === 1 ? '' : 's'}
              </span>
            </button>
          ))}
        </div>

        {other && (
          <BrioModalField label="Keep as the active item">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {pair.map((p) => (
                <label key={p.itemCode} style={{ display: 'flex', alignItems: 'baseline', gap: '0.45rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input type="radio" name="merge-keep" checked={keepCode === p.itemCode} onChange={() => setKeepCode(p.itemCode)} disabled={submitting} />
                  <code style={{ fontWeight: 600 }}>{p.itemCode}</code>
                  <span style={{ color: 'var(--brio-text-muted)' }}>{p.description || '—'}</span>
                </label>
              ))}
            </div>
          </BrioModalField>
        )}

        {kept && absorbed && (
          <div style={{ fontSize: '0.8rem', lineHeight: 1.5, padding: '0.5rem 0.7rem', borderRadius: 4, background: 'rgba(176,131,0,0.10)', color: '#8a6d00' }}>
            The {absorbed.versionCount} version{absorbed.versionCount === 1 ? '' : 's'} of <code>{absorbed.itemCode}</code> move
            under <code>{kept.itemCode}</code> as archived versions, listed under a divider
            reading &quot;Merged from {absorbed.itemCode}&quot;. They take the next free letters and <code>{kept.itemCode}</code>&apos;s
            classification. Their rates and project BOQ lines move with them, and live BOQs show the new codes;
            issued bill versions keep the codes they were issued with. <code>{absorbed.itemCode}</code> is retired and
            never issued again.
          </div>
        )}
      </BrioModalBody>
    </BrioModal>
  );
}

export default MergeItemsModal;
